import { ReactElement, useMemo } from 'react';
import { formatISO, startOfYear, endOfYear } from 'date-fns';
import { MonthRangeFilterChip } from 'components/filters/MonthRangeFilterChip';
import { ActivityFilterChip } from './ActivityFilterChip';
import { EnterpriseFilterChip } from 'components/filters/EnterpriseFilterChip';
import { FilterSeparator } from 'components/filters/FilterSeparator';
import { Visibility, VisibilityProps } from '../common';
import { usePeriod } from '../usePeriod';
import { columns } from './columns';
import { Row } from './types';
import { ReportTable } from 'components/ReportTable';
import { TableColumns } from '@ff-it/ui';
import { StateFilterChip } from '../StateFilterChip';
import { TypeFilterChip } from '../TypeFilterChip';

const resolveColumns = (visibility: Visibility): TableColumns<Row> => {
  switch (visibility) {
    case 'GLOBAL':
      return columns;
    case 'LOCAL':
      // no agency
      return columns.slice(1);
    case 'LOCAL_MANAGED':
    case 'LOCAL_TYPE':
      // no agency, no bonuses
      return columns.slice(1, 9);
  }
};

export function Activity({ visibility, ...props }: VisibilityProps): ReactElement {
  const { date_from: minDate, date_to: maxDate } = usePeriod();
  const initialFilter = {
    date_from: formatISO(startOfYear(new Date()), { representation: 'date' }),
    date_to: formatISO(endOfYear(new Date()), { representation: 'date' }),
  };
  const url = `reporting/activity/${visibility.toLowerCase()}${'type' in props ? `/${props.type}/` : '/'}`;
  const columns = useMemo(() => {
    return resolveColumns(visibility);
  }, [visibility]);

  return (
    <ReportTable
      defaultFilter={initialFilter}
      url={url}
      columns={columns}
      filterFields={
        <>
          <MonthRangeFilterChip
            label="Period"
            fromName="date_from"
            toName="date_to"
            minDate={minDate}
            maxDate={maxDate}
            defaultFrom={initialFilter.date_from}
            defaultTo={initialFilter.date_to}
          />
          <ActivityFilterChip name="activities" />
          {visibility === 'GLOBAL' && <EnterpriseFilterChip name="agency" isMulti defaultValueLabel="All agencies" />}
          <FilterSeparator />
          <StateFilterChip />
          {visibility !== 'LOCAL_TYPE' && <TypeFilterChip global={visibility === 'GLOBAL'} />}
        </>
      }
    />
  );
}
